<template>
    <div class="bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />
        <h2 class="text-xl font-semibold mb-4 text-gray-800">Update Miscellaneous Company</h2>
        <form @submit.prevent="save">

            <div class="mb-4">
                <label for="type" class="block text-gray-700 text-sm font-bold mb-2">Type:</label>
                <select id="type" v-model="type"
                    class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @change="onTypeChange" @blur="validateField('type')">
                    <option value="">-- Select Type --</option>
                    <option value="1">Bank</option>
                    <option value="2">Competitor</option>
                    <option value="3">Forwarder</option>
                    <option value="4">Subcon</option>
                </select>
                <p v-if="!valid.type" class="text-red-500 text-xs italic">{{ validationMessages.type }}</p>
            </div>
            <div class="mb-4">
                <label for="company" class="block text-gray-700 text-sm font-bold mb-2">Company:</label>
                <select id="company" v-model="company" :disabled="!type"
                    class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @change="onCompanyChange" @blur="validateField('company')">
                    <option value="">-- Select Company --</option>
                    <option v-for="co in filteredCompanyOptions" :key="co.id" :value="co.id">{{ co.entityName }}
                    </option>
                </select>
                <p v-if="!valid.company" class="text-red-500 text-xs italic">{{ validationMessages.company }}</p>
            </div>
            <div class="mb-4">
                <label for="companyText" class="block text-gray-700 text-sm font-bold mb-2">Company Text:</label>
                <input id="companyText" v-model="companyText"
                    class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @blur="validateField('companyText')" />
                <p v-if="!valid.companyText" class="text-red-500 text-xs italic">{{ validationMessages.companyText }}
                </p>
            </div>
            <div class="mb-4">
                <label for="address" class="block text-gray-700 text-sm font-bold mb-2">Address:</label>
                <textarea id="address" rows="3" v-model="address"
                    class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @blur="validateField('address')"></textarea>
                <p v-if="!valid.address" class="text-red-500 text-xs italic">{{ validationMessages.address }}</p>
            </div>

            <div class="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label for="country" class="block text-gray-700 text-sm font-bold mb-2">Country:</label>
                    <select id="country" v-model="country"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('country')">
                        <option value="">-- Select Country --</option>
                        <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.countryName }}</option>
                    </select>
                    <p v-if="!valid.country" class="text-red-500 text-xs italic">{{ validationMessages.country }}</p>
                </div>

                <div>
                    <label for="city" class="block text-gray-700 text-sm font-bold mb-2">City:</label>
                    <div class="flex items-center">
                        <select id="city" v-model="city"
                            class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                            @blur="validateField('city')">
                            <option value="">-- Select City --</option>
                            <option v-for="ci in cities" :key="ci.id" :value="ci.id">{{ ci.cityName }}</option>
                        </select>
                        <button @click="openModal('city')" type="button"
                            class="ml-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-3 rounded text-sm focus:outline-none focus:shadow-outline">+</button>
                    </div>
                    <p v-if="!valid.city" class="text-red-500 text-xs italic">{{ validationMessages.city }}</p>
                </div>

                <div>
                    <label for="zipCode" class="block text-gray-700 text-sm font-bold mb-2">ZIP Code:</label>
                    <input id="zipCode" v-model="zipCode"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('zipCode')" />
                    <p v-if="!valid.zipCode" class="text-red-500 text-xs italic">{{ validationMessages.zipCode }}</p>
                </div>
            </div>

            <div class="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label for="phone" class="block text-gray-700 text-sm font-bold mb-2">Phone:</label>
                    <input id="phone" v-model="phone"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('phone')" />
                    <p v-if="!valid.phone" class="text-red-500 text-xs italic">{{ validationMessages.phone }}</p>
                </div>
                <div>
                    <label for="email" class="block text-gray-700 text-sm font-bold mb-2">Email:</label>
                    <input id="email" v-model="email"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('email')" />
                    <p v-if="!valid.email" class="text-red-500 text-xs italic">{{ validationMessages.email }}</p>
                </div>
            </div>

            <div class="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label for="extension" class="block text-gray-700 text-sm font-bold mb-2">Extension:</label>
                    <input id="extension" v-model="extension"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('extension')" />
                    <p v-if="!valid.extension" class="text-red-500 text-xs italic">{{ validationMessages.extension }}
                    </p>
                </div>
                <div>
                    <label for="fax" class="block text-gray-700 text-sm font-bold mb-2">Fax:</label>
                    <input id="fax" v-model="fax"
                        class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                        @blur="validateField('fax')" />
                    <p v-if="!valid.fax" class="text-red-500 text-xs italic">{{ validationMessages.fax }}</p>
                </div>
            </div>

            <div class="mb-4">
                <label for="term" class="block text-gray-700 text-sm font-bold mb-2">Term:</label>
                <input id="term" type="number" v-model.number="term"
                    class="input-border shadow border rounded w-1/4 py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @blur="validateField('term')" />
                <span class="ml-2">days</span>
                <p v-if="!valid.term" class="text-red-500 text-xs italic">{{ validationMessages.term }}</p>
            </div>
            <div class="mb-4">
                <label for="memo" class="block text-gray-700 text-sm font-bold mb-2">Memo:</label>
                <textarea id="memo" rows="3" v-model="memo"
                    class="input-border shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                    @blur="validateField('memo')"></textarea>
                <p v-if="!valid.memo" class="text-red-500 text-xs italic">{{ validationMessages.memo }}</p>
            </div>
            <div class="mb-4 flex justify-end">
                <button type="button" @click="addContact"
                    class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">Add
                    Contact</button>
            </div>
            <div class="overflow-x-auto mb-4">
                <table class="min-w-full table-auto">
                    <thead>
                        <tr>
                            <th class="px-4 py-2 border-b sr-only">ID</th>
                            <th class="px-4 py-2 border-b">Name</th>
                            <th class="px-4 py-2 border-b">Handphone</th>
                            <th class="px-4 py-2 border-b">Email</th>
                            <th class="px-4 py-2 border-b">Bank</th>
                            <th class="px-4 py-2 border-b">Rekening</th>
                            <th class="px-4 py-2 border-b">NPWP</th>
                            <th class="px-4 py-2 border-b">Job Title</th>
                            <th class="px-4 py-2 border-b">Status</th>
                        </tr>
                    </thead>
                    <tbody v-if="activeContacts.length > 0">
                        <tr v-for="(contact, index) in activeContacts" :key="contact.id || `new-${index}`">
                            <td class="px-4 py-2 border-b sr-only">{{ contact.id }}</td>
                            <td class="px-4 py-2 border-b"><input type="text" v-model="contact.name"
                                    class="w-full border rounded p-2" placeholder="Enter Name" /></td>
                            <td class="px-4 py-2 border-b"><input type="text" v-model="contact.handphone"
                                    class="w-full border rounded p-2" placeholder="Enter Handphone" /></td>
                            <td class="px-4 py-2 border-b">
                                <input type="email" v-model="contact.email" class="w-full border rounded p-2"
                                    placeholder="Enter Email (Optional)" />
                            </td>
                            <td class="px-4 py-2 border-b">
                                <select v-model="contact.bankId" class="w-full border rounded p-2">
                                    <option value="">-- Pilih Bank --</option>
                                    <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.bankName }}
                                    </option>
                                </select>
                            </td>
                            <td class="px-4 py-2 border-b"><input type="text" v-model="contact.rekening"
                                    class="w-full border rounded p-2" placeholder="Enter Rekening" /></td>
                            <td class="px-4 py-2 border-b"><input type="text" v-model="contact.npwp"
                                    class="w-full border rounded p-2" placeholder="Enter NPWP" /></td>
                            <td class="px-4 py-2 border-b"><input type="text" v-model="contact.jobTitle"
                                    class="w-full border rounded p-2" placeholder="Enter Job Title" /></td>
                            <td class="px-4 py-2 border-b"><button type="button" @click="deleteContact(contact)"
                                    class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">Delete</button>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="9" class="px-4 py-2 text-center text-gray-500">No contacts added yet.</td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="!valid.contacts" class="text-red-500 text-xs italic mt-1">{{ validationMessages.contacts }}</p>
            </div>
            <div class="flex justify-center mt-4">
                <div class="flex items-center mr-auto">
                    <input type="checkbox" id="markForDeletion" v-model="isMarkedForDeletion"
                        class="form-checkbox h-4 w-4 text-red-600" />
                    <label for="markForDeletion" class="ml-2 text-sm font-medium text-red-600">Delete Entity
                        (Permanently)</label>
                </div>

                <button type="submit" :disabled="!isFormValid && !isMarkedForDeletion"
                    class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2">Update</button>
                <button type="button" @click="cancel"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">Cancel</button>
            </div>

        </form>

        <add-item-modal :visible="isModalVisible" :title="modalConfig.title" :label="modalConfig.label"
            :existing-items="modalConfig.existingItems" :name-key="modalConfig.nameKey" @close="closeModal"
            @save="handleModalSave" />

    </div>
</template>

<script>
/* eslint-disable no-case-declarations */
import axios from 'axios';
import Swal from 'sweetalert2';
import AddItemModal from './AddItemModal.vue';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
    name: 'SubconUpdate',
    components: { AddItemModal, MonkeyLoader },

    props: {
        existingData: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            type: '',
            company: '',
            companyText: '',
            address: '',
            country: '',
            city: '',
            zipCode: '',
            phone: '',
            fax: '',
            extension: '',
            email: '',
            term: 1,
            memo: '',
            isMarkedForDeletion: false, // Properti baru untuk checkbox delete

            companyOptions: [],
            countries: [],
            cities: [],
            banks: [],
            contacts: [],

            valid: {
                type: true, company: true, companyText: true, address: true,
                country: true, city: true,
                zipCode: true, phone: true,
                email: true,
                extension: true,
                fax: true,
                term: true, memo: true, contacts: true,
            },
            validationMessages: {
                type: '', company: '', companyText: '', address: '',
                country: '', city: '',
                zipCode: '', phone: '', email: '', extension: '',
                fax: '', term: '', memo: '', contacts: '',
            },
            isFormValid: false,

            isModalVisible: false,
            modalConfig: {
                title: '',
                label: '',
                type: '',
                existingItems: [],
                nameKey: ''
            },
        };
    },

    computed: {
        filteredCompanyOptions() {
            return this.type
                ? this.companyOptions.filter(c => String(c.entityType) === this.type)
                : [];
        },
        activeContacts() {
            return this.contacts.filter(contact => contact.status !== false);
        }
    },

    methods: {
        addContact() {
            this.contacts.push({
                id: undefined,
                name: '', handphone: '', email: '', bankId: null, rekening: '', npwp: '', jobTitle: '',
                status: true
            });
            this.validateField('contacts');
        },

        async deleteContact(contactToDelete) {
            if (contactToDelete.id !== undefined && contactToDelete.id !== null) {
                // eslint-disable-next-line no-case-declarations
                const result = await Swal.fire({
                    title: 'Are you sure?',
                    text: `You are about to deactivate contact "${contactToDelete.name}". This cannot be undone.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, deactivate it!'
                });

                if (result.isConfirmed) {
                    try {
                        await axios.put(
                            `${window.location.origin}/api/contacts/${contactToDelete.id}`,
                            { status: false },
                            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                        const index = this.contacts.findIndex(c => c.id === contactToDelete.id);
                        if (index !== -1) {
                            this.contacts[index].status = false;
                        }
                        Swal.fire('Deactivated!', `Contact "${contactToDelete.name}" has been deactivated.`, 'success');
                        this.validateField('contacts');
                    } catch (error) {
                        console.error('Failed to deactivate contact', error);
                        Swal.fire('Error!', 'Failed to deactivate contact.', 'error');
                    }
                }
            } else {
                const index = this.contacts.indexOf(contactToDelete);
                if (index > -1) {
                    this.contacts.splice(index, 1);
                }
                this.validateField('contacts');
            }
        },

        /* ---------- Validasi ---------- */
        validateField(fieldName) {
            this.valid[fieldName] = true;
            this.validationMessages[fieldName] = '';

            switch (fieldName) {
                case 'type':
                    if (!this.type) {
                        this.valid.type = false;
                        this.validationMessages.type = 'Type is required.';
                    }
                    break;
                case 'company':
                    if (!this.company) {
                        this.valid.company = false;
                        this.validationMessages.company = 'Company is required.';
                    }
                    break;
                case 'companyText':
                    if (!this.companyText || !this.companyText.trim()) {
                        this.valid.companyText = false;
                        this.validationMessages.companyText = 'Company Text is required.';
                    }
                    break;
                case 'address':
                    if (!this.address || !this.address.trim()) {
                        this.valid.address = false;
                        this.validationMessages.address = 'Address is required.';
                    }
                    break;
                case 'country':
                    if (!this.country) {
                        this.valid.country = false;
                        this.validationMessages.country = 'Country is required.';
                    }
                    break;
                case 'city':
                    if (!this.city) {
                        this.valid.city = false;
                        this.validationMessages.city = 'City is required.';
                    }
                    break;
                case 'zipCode':
                    if (!this.zipCode || !this.zipCode.trim()) {
                        this.valid.zipCode = false;
                        this.validationMessages.zipCode = 'ZIP Code is required.';
                    }
                    break;
                case 'phone':
                    if (!this.phone || !this.phone.trim()) {
                        this.valid.phone = false;
                        this.validationMessages.phone = 'Phone Number is required.';
                    }
                    break;
                case 'email':
                    if (this.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
                        this.valid.email = false;
                        this.validationMessages.email = 'Invalid email format.';
                    }
                    break;
                case 'fax':
                case 'extension':
                    break;
                case 'term':
                    if (this.term === null || this.term === '' || isNaN(this.term) || parseInt(this.term) < 1) {
                        this.valid.term = false;
                        this.validationMessages.term = 'Term must be a positive number (days).';
                    }
                    break;
                case 'memo':
                    if (!this.memo || !this.memo.trim()) {
                        this.valid.memo = false;
                        this.validationMessages.memo = 'Memo is required.';
                    }
                    break;
                case 'contacts':
                    const activeContacts = this.contacts.filter(contact => contact.status !== false);
                    if (activeContacts.length === 0) {
                        this.valid.contacts = false;
                        this.validationMessages.contacts = 'At least one active contact must be added.';
                    } else {
                        let allContactsValid = true;
                        for (let i = 0; i < activeContacts.length; i++) {
                            const contact = activeContacts[i];
                            if (
                                !contact.name || !contact.name.trim() ||
                                !contact.handphone || !contact.handphone.trim() ||
                                !contact.rekening || !contact.rekening.trim() ||
                                !contact.npwp || !contact.npwp.trim() ||
                                !contact.jobTitle || !contact.jobTitle.trim() ||
                                contact.bankId === null || contact.bankId === undefined || contact.bankId === ''
                            ) {
                                allContactsValid = false;
                                this.valid.contacts = false;
                                this.validationMessages.contacts = `All required fields (Name, Handphone, Rekening, NPWP, Job Title, Bank) for active contact #${i + 1} must be filled.`;
                                break;
                            }
                            if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
                                allContactsValid = false;
                                this.valid.contacts = false;
                                this.validationMessages.contacts = `Invalid email format for active contact #${i + 1}.`;
                                break;
                            }
                        }
                        this.valid.contacts = allContactsValid;
                        if (allContactsValid) {
                            this.validationMessages.contacts = '';
                        }
                    }
                    break;
            }
            this.updateFormValidity();
        },

        updateFormValidity() {
            // Form dianggap valid jika isMarkedForDeletion adalah true
            // ATAU jika semua validasi field lainnya adalah true
            this.isFormValid = Object.values(this.valid).every(v => v) || this.isMarkedForDeletion;
        },

        refreshValidation() {
            Object.keys(this.valid).forEach(this.validateField);
        },

        /* ---------- Helpers ---------- */
        resetDetails() {
            Object.assign(this, {
                companyText: '', address: '', country: '', city: '',
                zipCode: '', phone: '', fax: '', extension: '', email: '', term: 1, memo: ''
            });
            this.contacts = [];
            this.isMarkedForDeletion = false; // Reset status delete
            this.refreshValidation();
        },

        fillFromEntity(e) {
            Object.assign(this, {
                companyText: e.entityName || '',
                address: e.entityAddress || '',
                country: String(e.countryId || ''),
                city: String(e.cityId || ''),
                zipCode: e.zipCode || '',
                phone: e.entityPhone || '',
                fax: e.entityFax || '',
                extension: e.entityExt || '',
                email: e.entityEmail || '',
                term: Number(e.term || 1),
                memo: e.memo || ''
            });
            // Pastikan isMarkedForDeletion tidak diubah saat mengisi data dari entity
            // Karena ini adalah pilihan user untuk menghapus, bukan bagian dari data entitas
            this.refreshValidation();
        },

        /* ---------- Select Handlers ---------- */
        onTypeChange() {
            this.company = '';
            this.resetDetails();
            this.validateField('type');
        },

        async onCompanyChange() {
            this.validateField('company');
            if (!this.company) {
                this.resetDetails();
                this.contacts = [];
                this.isMarkedForDeletion = false; // Reset status delete saat perusahaan tidak dipilih
                return;
            }

            const cached = this.companyOptions.find((c) => String(c.id) === String(this.company));
            if (cached) {
                this.fillFromEntity(cached);
            }

            try {
                const { data } = await axios.get(`${window.location.origin}/api/entities/${this.company}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });

                this.fillFromEntity(data.entity);
                this.contacts = data.contacts.map(contact => ({
                    id: contact.id,
                    name: contact.contactName || '',
                    handphone: contact.contactphone || '',
                    email: contact.contactemail || '',
                    bankId: contact.bankId || null,
                    rekening: contact.contactRek || '',
                    npwp: contact.contactnpwp || '',
                    jobTitle: contact.contactjt || '',
                    status: contact.status !== undefined ? contact.status : true
                }));
                this.isMarkedForDeletion = false; // Reset status delete saat data baru dimuat
                this.validateField('contacts');
            } catch (e) {
                console.error('Fetch entity failed', e);
                Swal.fire('Error!', 'Failed to load company details.', 'error');
            }
        },

        /* ---------- Data Fetch ---------- */
        async fetchList(url, target) {
            try {
                const { data } = await axios.get(url, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (Array.isArray(data)) this[target] = data;
            } catch (e) {
                console.error(`Fetch ${target} failed`, e);
            }
        },

        /* ---------- Submit Form ---------- */
        async save() {
            // Jika checkbox "Delete Entity" dicentang
            if (this.isMarkedForDeletion) {
                const result = await Swal.fire({
                    title: 'Are you absolutely sure?',
                    text: `You are about to permanently delete the entity "${this.companyText}". This action cannot be undone.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33', // Merah untuk delete
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, delete it!'
                });

                if (result.isConfirmed) {
                    try {
                        await axios.delete(
                            `${window.location.origin}/tblentities/${this.company}`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                        Swal.fire('Deleted!', 'The entity has been permanently deleted.', 'success');
                        window.location.reload(); // Reload halaman atau redirect
                    } catch (e) {
                        console.error('Failed to delete entity', e);
                        Swal.fire('Error!', 'Failed to delete entity. Please ensure there are no dependent records in other tables.', 'error');
                    }
                }
                return; // Hentikan proses save jika tombol delete dicentang (baik dikonfirmasi maupun tidak)
            }

            // Jika checkbox "Delete Entity" TIDAK dicentang, lanjutkan dengan proses Update
            this.refreshValidation();
            this.updateFormValidity(); // Perlu dicek lagi setelah refreshValidation

            if (!this.isFormValid) {
                const firstInvalidField = Object.keys(this.valid).find(key => !this.valid[key]);
                if (firstInvalidField) {
                    let refElement = null;
                    refElement = document.getElementById(firstInvalidField);

                    if (firstInvalidField === 'company' && !refElement) {
                        refElement = document.getElementById('company');
                    } else if (firstInvalidField === 'companyText' && !refElement) {
                        refElement = document.getElementById('companyText');
                    }

                    if (refElement && typeof refElement.focus === 'function') {
                        this.$nextTick(() => {
                            refElement.focus();
                        });
                    }
                }
                Swal.fire({
                    icon: 'warning',
                    title: 'Validation Error',
                    text: 'Please fill in all required fields correctly to update.',
                });
                return;
            }

            if (!this.company) {
                Swal.fire('Warning', 'Please select a company first.', 'warning');
                return;
            }

            const contactsPayload = this.contacts.map(contact => {
                const payloadContact = {
                    ContactName: contact.name,
                    ContactPhone: contact.handphone,
                    ContactEmail: contact.email || null,
                    BankId: parseInt(contact.bankId) || null,
                    ContactRek: contact.rekening,
                    ContactNPWP: contact.npwp,
                    ContactJobTitle: contact.jobTitle,
                    Status: contact.status
                };
                if (contact.id !== undefined && contact.id !== null) {
                    payloadContact.id = contact.id;
                }
                return payloadContact;
            });

            const payload = {
                EntityName: this.companyText,
                EntityType: parseInt(this.type),
                EntityAddress: this.address,
                CountryId: parseInt(this.country),
                CityId: parseInt(this.city),
                ZipCode: this.zipCode,
                EntityPhone: this.phone,
                EntityFax: this.fax || null,
                EntityExt: this.extension || null,
                EntityEmail: this.email || null,
                Term: this.term.toString(),
                Memo: this.memo,
                Contacts: contactsPayload // Pastikan ini 'Contacts' sesuai DTO C# Anda
            };

            try {
                await axios.put(
                    `${window.location.origin}/api/entities/${this.company}`,
                    payload,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                Swal.fire('Success!', 'Data updated successfully.', 'success');
                window.location.reload();
            } catch (e) {
                console.error('Update failed', e);
                Swal.fire('Error!', 'Failed to update data. Please try again.', 'error');
            }
        },

        cancel() {
            this.$emit('cancel');
        },

        // --- Modal Methods ---
        openModal(type) {
            switch (type) {
                case 'city':
                    this.modalConfig = { title: 'Add New City', label: 'City Name', type: 'city', existingItems: this.cities, nameKey: 'cityName' };
                    break;
                default:
                    console.error("Unknown modal type:", type);
                    return;
            }
            this.isModalVisible = true;
        },

        closeModal() {
            this.isModalVisible = false;
        },

        async handleModalSave(newItemName) {
            const { type } = this.modalConfig;
            let payload = {};
            let url = '';
            let listToUpdate = '';
            let fieldToSelect = '';

            switch (type) {
                case 'city':
                    url = `${window.location.origin}/tblcities`;
                    payload = { cityName: newItemName, countryId: this.country };
                    listToUpdate = 'cities';
                    fieldToSelect = 'city';
                    break;
                default:
                    console.error("Unknown modal type:", type);
                    return;
            }

            try {
                console.log('Attempting to save new item:', payload);
                const response = await axios.post(url, payload, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });

                const newRecord = response.data;
                Swal.fire('Success!', `${this.modalConfig.label} saved successfully!`, 'success');
                this.closeModal();

                switch (type) {
                    case 'city':
                        await this.fetchList(`${window.location.origin}/tbl${listToUpdate}`, listToUpdate);
                        this[fieldToSelect] = String(newRecord.id);
                        this.validateField(fieldToSelect);
                        break;
                }
            } catch (error) {
                console.error(`Failed to save ${type}:`, error);
                if (error.response && error.response.status === 409) {
                    Swal.fire('Conflict', `A ${type} with this name already exists.`, 'error');
                } else {
                    Swal.fire('Error', `Failed to save ${this.modalConfig.label}.`, 'error');
                }
            }
        },
    },

    mounted() {
        this.fetchList(`${window.location.origin}/tblcountries`, 'countries');
        this.fetchList(`${window.location.origin}/tblcities`, 'cities');
        this.fetchList(`${window.location.origin}/tblentities`, 'companyOptions');
        this.fetchList(`${window.location.origin}/api/banks`, 'banks');

        if (this.existingData) {
            Object.assign(this, {
                type: String(this.existingData.entityType || ''),
                company: String(this.existingData.id || ''),
                companyText: this.existingData.entityName || '',
                address: this.existingData.entityAddress || '',
                country: String(this.existingData.countryId || ''),
                city: String(this.existingData.cityId || ''),
                zipCode: this.existingData.zipCode || '',
                phone: this.existingData.entityPhone || '',
                fax: this.existingData.entityFax || '',
                extension: this.existingData.entityExt || '',
                email: this.existingData.entityEmail || '',
                term: Number(this.existingData.term || 1),
                memo: this.existingData.memo || ''
            });
            if (this.existingData.tblContacts) {
                this.contacts = this.existingData.tblContacts.map(contact => ({
                    id: contact.id,
                    name: contact.contactName || '',
                    handphone: contact.contactphone || '',
                    email: contact.contactemail || '',
                    bankId: contact.bankId || null,
                    rekening: contact.contactRek || '',
                    npwp: contact.contactnpwp || '',
                    jobTitle: contact.contactjt || '',
                    status: contact.status !== undefined ? contact.status : true
                }));
            }
        }
        this.refreshValidation();
    },

    watch: {
        company(newVal) {
            if (newVal) {
                this.onCompanyChange();
            } else {
                this.resetDetails();
                this.contacts = [];
            }
            this.validateField('company');
        },
        type: function () { this.validateField('type'); },
        companyText: function () { this.validateField('companyText'); },
        address: function () { this.validateField('address'); },
        country: function () { this.validateField('country'); },
        city: function () { this.validateField('city'); },
        zipCode: function () { this.validateField('zipCode'); },
        phone: function () { this.validateField('phone'); },
        email: function () { this.validateField('email'); },
        extension: function () { this.validateField('extension'); },
        fax: function () { this.validateField('fax'); },
        term: function () { this.validateField('term'); },
        memo: function () { this.validateField('memo'); },
        contacts: {
            handler: function () { this.validateField('contacts'); },
            deep: true
        },
        isFormValid(newValue) {
            const saveButton = this.$el.querySelector('button[type="submit"]');
            if (saveButton) {
                // Tombol save diaktifkan jika form valid ATAU jika isMarkedForDeletion true
                saveButton.disabled = !(newValue || this.isMarkedForDeletion);
            }
        },
        // Watcher untuk isMarkedForDeletion agar mengupdate isFormValid
        isMarkedForDeletion(newValue) {
            this.updateFormValidity();
            // Ketika dicentang, mungkin kita tidak butuh validasi field lain, fokus pada penghapusan
            // Ketika tidak dicentang, validasi kembali semua field form
            if (!newValue) {
                this.refreshValidation();
            } else {
                // Jika dicentang, kita mungkin ingin mengabaikan pesan validasi form lainnya
                // agar tombol Update bisa diklik hanya untuk delete.
                // Ini adalah keputusan desain UI. Untuk saat ini, kita biarkan validasi tampil.
            }
        }
    }
};
</script>

<style scoped>
/* Tambahkan atau sesuaikan gaya v-select di sini jika diperlukan */
/* Contoh untuk membuat v-select terlihat seperti input lainnya */
.v-select-custom-style .vs__dropdown-toggle {
    border: 1px solid #d2d6dc;
    /* Warna border default Tailwind gray-300 */
    border-radius: 0.25rem;
    /* rounded */
    padding: 0.5rem 0.75rem;
    /* py-2 px-3 */
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    /* shadow */
    background-color: #fff;
}

.v-select-custom-style .vs__selected-options {
    padding: 0;
}

.v-select-custom-style .vs__selected {
    margin: 0;
    padding: 0;
}

.v-select-custom-style .vs__search::placeholder,
.v-select-custom-style .vs__dropdown-toggle,
.v-select-custom-style .vs__dropdown-menu {
    color: #4a5568;
    /* text-gray-700 */
}

.v-select-custom-style .vs__search:focus {
    outline: none;
    border-color: #3b82f6;
    /* focus:border-blue-500 */
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
    /* focus:ring-blue-500 */
}
</style>